import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CalendarDays } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, SelectF, CheckF } from "../admin/AFields";
import { Modal } from "../admin/Participants";

export default function StudioCalendar() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/greetings/occasions").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/greetings/occasions/save", editing); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="studio-calendar">
      <SEO title="Greeting Calendar" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Greeting Calendar</h1>
          <p className="mt-1 text-sm text-slate-500">Festivals, national days, awareness days, organisation events and custom dates. Verify festival dates each year.</p>
        </div>
        <button onClick={() => setEditing({ name: "", date: "", category: "Festivals", recurring: true, language: "bilingual", active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="cal-new-btn"><CalendarDays size={15} /> Add Occasion</button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((o) => (
          <div key={o.id} className={`rounded-lg border border-gray-200 bg-white p-4 ${o.active === false ? "opacity-55" : ""}`} data-testid={`cal-row-${o.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-navy">{o.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">{o.date || "flexible"} · {o.category}{o.recurring ? " · yearly" : ""}</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setEditing(o)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`cal-edit-${o.id}`}><Pencil size={13} /></button>
                <button onClick={async () => { if (window.confirm(`Delete "${o.name}"?`)) { await api.delete(`/greetings/occasions/${o.id}`); load(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`cal-del-${o.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title="Occasion" testid="cal-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Occasion" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextF label="Date" type="date" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
            <SelectF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={["Festivals", "National Days", "International Days", "Awareness Days", "Education Days", "Health Days", "Environment Days", "Social Justice Days", "Foundation Events", "Birthdays", "Anniversaries", "Custom"]} />
            <SelectF label="Language" value={editing.language} onChange={(v) => setEditing({ ...editing, language: v })} options={["english", "hindi", "bilingual"]} />
            <CheckF label="Recurring (yearly)" value={editing.recurring} onChange={(v) => setEditing({ ...editing, recurring: v })} />
            <CheckF label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="cal-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
